import React, { useMemo, useState } from 'react';
import { AppBreadcrumb, AppCard, AppContainer, AppSpin } from '@/components/atoms';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { CreateClinicForm } from '@/components/organisms';
import { useAdminClinic, useAdminUpdateClinic } from '@/hooks/admin/useAdminClinics';
import useAppToast from '@/hooks/useAppToast';
import { SERVICE_TYPE_A2, SERVICE_TYPE_BOTH, SERVICE_TYPE_R2 } from '@/constants/serviceType';
import { AuditInfo } from '@/components/molecules';

type ValidationErrorResponse = {
  message?: string;
  errors?: Record<string, string[]>;
};

type ErrorWithResponse = {
  response?: {
    data?: ValidationErrorResponse;
  };
};

export type ServiceType = 'A2' | 'R2' | 'BOTH';

export interface Massager {
  id: number;
  serial_number: number;
  service_type: ServiceType;
  name: string;
  massage_license_number: string | null;
  available_from: string;
  available_to: string | null;
}
export interface CreateClinicFormData {
  site_type: string | null;
  service_type: 'A2' | 'R2' | 'BOTH' | null;
  clinic_number: string;
  available_from: string;
  available_to?: string | null;
  is_excluded_transfer: 0 | 1;
  is_none_disp_delegation_message?: 0 | 1;
  is_none_disp_delegation2_message?: 0 | 1;
  is_none_disp_delegation3_message?: 0 | 1;
  is_enable_checkin?: 0 | 1;
  checkin_available_from?: string | null;
  checkin_available_to?: string | null;
}

const EditClinicPage: React.FC = () => {
  const navigate = useNavigate();
  const { site, id } = useParams<{ site: string; id: string }>();
  const updateClinicMutation = useAdminUpdateClinic();
  const [apiFieldErrors, setApiFieldErrors] = useState<Record<string, string> | null>(null);
  const toast = useAppToast();

  const isSuperAdminRoute = site === 'super-admin';
  const clinicId = id ? parseInt(id, 10) : 0;

  // Fetch clinic data
  const { data: clinicData, isLoading: isFetching } = useAdminClinic(site || '', clinicId);

  const siteForBreadcrumb = site ?? 'medix';

  const breadcrumbItems = useMemo(
    () => [
      {
        title: (
          <Link to={`/${siteForBreadcrumb}/admin/clinics`} style={{ color: 'inherit' }}>
            治療院
          </Link>
        ),
      },
      { title: `編集: ${clinicId}` },
    ],
    [siteForBreadcrumb, clinicId]
  );

  // Format initial values
  const initialValues = useMemo<Partial<CreateClinicFormData> | undefined>(() => {
    // Check if clinicData and clinicData.data exist
    // The API response is wrapped in { success: true, data: { ... } }
    const data = (clinicData as any)?.data;
    if (!data) return undefined;

    // Convert API data to form data format
    return {
      site_type: data.site_type,
      service_type:
        data.service_type?.toUpperCase() === SERVICE_TYPE_A2
          ? SERVICE_TYPE_A2
          : data.service_type?.toUpperCase() === SERVICE_TYPE_R2
            ? SERVICE_TYPE_R2
            : data.service_type?.toUpperCase() === SERVICE_TYPE_BOTH
              ? SERVICE_TYPE_BOTH
              : null,
      clinic_number: data.clinic_number,
      available_from: data.available_from ?? '',
      available_to: data.available_to,
      is_excluded_transfer: data.is_excluded_transfer ? 1 : 0,
      is_none_disp_delegation_message: data.is_none_disp_delegation_message ? 1 : 0,
      is_none_disp_delegation2_message: data.is_none_disp_delegation2_message ? 1 : 0,
      is_none_disp_delegation3_message: data.is_none_disp_delegation3_message ? 1 : 0,
      is_enable_checkin: data.is_enable_checkin ? 1 : 0,
      checkin_available_from: data.checkin_available_from,
      checkin_available_to: data.checkin_available_to,
    };
  }, [clinicData]);

  const handleSubmit = async (data: CreateClinicFormData) => {
    if (!site || !clinicId) {
      toast.error('サイト情報 hoặc 治療院IDが取得できませんでした。');
      return;
    }

    try {
      const response = (await updateClinicMutation.mutateAsync({
        site,
        id: clinicId,
        data,
      })) as {
        message?: string;
        data?: { id?: number };
      };

      const successMessage = response?.message ?? '治療院が正常に更新されました。';
      toast.success(successMessage);

      // Stay on the edit page
      // navigate(`/${site}/admin/clinics/${clinicId}`, { replace: true });
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as ErrorWithResponse;
        const data = axiosError.response?.data;
        const clinicNumberError = data?.errors?.clinic_number?.[0];
        if (clinicNumberError) {
          setApiFieldErrors({ clinic_number: clinicNumberError });
        }
        const serviceTypeError = data?.errors?.service_type?.[0];
        if (serviceTypeError) {
          toast.error(serviceTypeError);
          return;
        }
        if (clinicNumberError) {
          return;
        }
      }
      toast.error('エラーが発生しました。');
      console.error(err);
    }
  };

  if (isFetching) {
    return (
      <AppContainer className="flex justify-center">
        <AppSpin size="large" />
      </AppContainer>
    );
  }

  return (
    <AppContainer className="space-y-5">
      <AppBreadcrumb items={breadcrumbItems} separator="/" />
      <AppCard title={`治療院編集: ${clinicId}`} className="w-full">
        <CreateClinicForm
          onSubmit={handleSubmit}
          isLoading={updateClinicMutation.isPending}
          siteForSiteTypes={isSuperAdminRoute ? undefined : (site ?? undefined)}
          initialValues={initialValues}
          onCancel={() => navigate(`/${site}/admin/clinics/${clinicId}`)}
          apiFieldErrors={apiFieldErrors}
          onClearFieldError={field =>
            setApiFieldErrors(prev => {
              if (!prev) return null;
              const next = { ...prev };
              delete next[field];
              return Object.keys(next).length ? next : null;
            })
          }
          hasClinicUsers={Array.isArray(clinicData?.data?.users) && clinicData.data.users.length > 0}
          hasLocationA2={Array.isArray(clinicData?.data?.clinic_locations) && clinicData.data.clinic_locations.some(
            (loc: { service_type?: string }) => {
              const st = (loc.service_type ?? '').toUpperCase();
              return st === SERVICE_TYPE_A2 || st === SERVICE_TYPE_BOTH;
            }
          )}
          hasLocationR2={Array.isArray(clinicData?.data?.clinic_locations) && clinicData.data.clinic_locations.some(
            (loc: { service_type?: string }) => {
              const st = (loc.service_type ?? '').toUpperCase();
              return st === SERVICE_TYPE_R2 || st === SERVICE_TYPE_BOTH;
            }
          )}
        />
        <AuditInfo
          createdBy={clinicData?.data?.created_by}
          updatedBy={clinicData?.data?.updated_by}
          updatedAt={clinicData?.data?.updated_at}
        />
      </AppCard>
    </AppContainer>
  );
};

export default EditClinicPage;
